import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
} from "react";
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { apiService } from "../services/api.service";
import { getSupabaseClient } from "../config/supabase";

interface User {
  id: string;
  email: string;
  isAdmin: boolean;
  isAnonymous?: boolean;
  isEmailSent?: boolean; // Add this property to support the login flow state
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  isAuthChecked: boolean;
  token: string | null;
  login: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyToken: (token: string) => Promise<{ user: User }>;
  requiresAuth: (feature: 'save' | 'share' | 'collaborate' | 'admin') => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const ANONYMOUS_USER: User = {
  id: 'anonymous',
  email: '',
  isAdmin: false,
  isAnonymous: true,
};

const mapSupabaseUser = (supabaseUser: SupabaseUser): User => ({
  id: supabaseUser.id,
  email: supabaseUser.email ?? "",
  isAdmin: supabaseUser.email?.endsWith("@metarouter.io") ?? false,
  isAnonymous: false,
});

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(ANONYMOUS_USER);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  // Initialize auth state from Supabase session
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await getSupabaseClient().auth.getSession();
        if (error) throw error;

        if (session?.user) {
          const mappedUser = mapSupabaseUser(session.user);
          setUser(mappedUser);
          setToken(session.access_token);
          apiService.setToken(session.access_token);
        } else {
          setUser(ANONYMOUS_USER);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        setUser(ANONYMOUS_USER);
      } finally {
        setIsLoading(false);
        setIsAuthChecked(true);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = getSupabaseClient().auth.onAuthStateChange(async (event: string, session: Session | null) => {
      console.log('Auth state changed:', event);

      if (session?.user) {
        const mappedUser = mapSupabaseUser(session.user);
        setUser(mappedUser);
        setToken(session.access_token);
        apiService.setToken(session.access_token);
      } else {
        setUser(ANONYMOUS_USER);
        setToken(null);
        apiService.setToken(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = useCallback(async (email: string) => {
    try {
      setIsLoading(true);
      
      // Get the current origin for local development fallback
      const currentOrigin = window.location.origin;
      // Use the configured app URL or fall back to current origin
      const siteUrl = import.meta.env.VITE_APP_URL || currentOrigin;
      
      // Create the full redirect URL
      const redirectUrl = new URL('/verify', siteUrl);
      
      const { error } = await getSupabaseClient().auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl.toString()
        }
      });
      
      if (error) throw error;
      
      // Temporarily set email in user state
      // Mark this as a temporary user with isEmailSent flag to indicate we're in the middle of auth flow
      setUser({
        id: 'temp',
        email,
        isAdmin: email.endsWith('@metarouter.io'),
        isAnonymous: true, // Keep as anonymous until fully authenticated
        isEmailSent: true, // Add this flag to indicate email has been sent
      });
    } catch (error) {
      console.error("Error sending magic link:", error);
      setUser(ANONYMOUS_USER);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const { error } = await getSupabaseClient().auth.signOut();
      if (error) throw error;
      
      setUser(ANONYMOUS_USER);
      setToken(null);
      apiService.setToken(null);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  }, []);

  const verifyToken = useCallback(async (sessionToken: string): Promise<{ user: User }> => {
    try {
      console.log('AuthContext: Verifying session token');
      
      // Get user from Supabase first
      const { data: { user: supabaseUser }, error: supabaseError } = await getSupabaseClient().auth.getUser(sessionToken);
      
      if (supabaseError || !supabaseUser) {
        throw new Error(supabaseError?.message || "No user found in session");
      }

      // Then verify with our backend
      await apiService.verifyToken(sessionToken);

      // Update API service token
      apiService.setToken(sessionToken);

      const mappedUser = {
        id: supabaseUser.id,
        email: supabaseUser.email ?? "",
        isAdmin: supabaseUser.email?.endsWith("@metarouter.io") ?? false,
        isAnonymous: false,
      };
      
      return { user: mappedUser };
    } catch (error) {
      console.error("Token verification failed:", error);
      
      if (error instanceof Error && error.message.includes('expired')) {
        throw new Error("Your session has expired. Please log in again.");
      }
      throw error;
    }
  }, []);

  const requiresAuth = useCallback((feature: 'save' | 'share' | 'collaborate' | 'admin'): boolean => {
    if (!user) return true;
    if (user.isAnonymous) return true;
    if (feature === 'admin') return !user.isAdmin;
    return false;
  }, [user]);

  const value = {
    user,
    isAuthenticated: !!user && !user.isAnonymous,
    isAdmin: user?.isAdmin ?? false,
    isLoading,
    isAuthChecked,
    token,
    login,
    logout,
    verifyToken,
    requiresAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Hook for protected routes - only used for routes that require authentication
export function useRequireAuth() {
  const auth = useAuth();

  if (!auth.isAuthenticated) {
    throw new Error("This route requires authentication");
  }

  return auth;
}

// Hook for admin routes
export function useRequireAdmin() {
  const auth = useAuth();

  if (!auth.isAdmin) {
    throw new Error("This route requires admin privileges");
  }

  return auth;
}
